/**
 * The server's UTC time, as best this browser can tell, from a local clock that
 * may be arbitrarily wrong (#763).
 */

import { observationFromPage, readPageTimings } from './server-time-from-page'
import {
    observationFromPersistedOffset,
    readPersistedOffset,
    rememberOffset,
    PERSISTED_OFFSET_UNCERTAINTY_MS,
} from './server-time-persisted'

/**
 * Something we learned about the server's clock, from wherever. The clock does
 * not care which source this came from — a value rendered into the page, an
 * HTTP `Date` header, a SignalR round trip — only how wrong it might be.
 *
 * The source modules import this type back from here, which is a cycle on
 * paper and none at runtime: TypeScript elides an import used only in type
 * position. Keep it that way. Importing a *value* from this module into one of
 * them would make the cycle real, and this module runs code at load — the half
 * that lost the race would see `undefined` where a function should be, in the
 * one place there is no later call to recover on.
 */
export interface ServerTimeObservation {
    /** What the server said its UTC time was, in ms. */
    serverUtcMs: number;
    /** The local clock reading at the moment `serverUtcMs` was taken. */
    clientUtcMs: number;
    /** How far `serverUtcMs` could be from the truth, in ms. */
    uncertaintyMs: number;
}

export interface CorrectedClock {
    /** The server's UTC time in ms. */
    now(): number;
    /** Tell the clock something about the server's time. */
    observe(observation: ServerTimeObservation): void;
}

export interface CorrectedClockOptions {
    /** Reads the local clock. Production passes `Date.now`. */
    localNow: () => number;
}

/**
 * The fraction of elapsed local time by which a backward correction is repaid.
 * The clock therefore still runs forward at (1 - SLEW_RATE) of real speed while
 * it repays, rather than standing still until the local clock catches up.
 *
 * Standing still is the tempting alternative and it is worse. A stalled clock
 * hands one identical stamp to every id minted in the window, so `ts` stops
 * separating them and pigeon's history insertion falls back to cid for the lot.
 * Repaying keeps every reading distinct.
 */
const SLEW_RATE = 0.1;

/**
 * How long an adopted observation stays authoritative enough to turn away a
 * coarser one. Past this, any fresh reading is preferred to a stale fine one,
 * because the local clock they are both measured against may have been stepped
 * in between.
 */
const OBSERVATION_FRESHNESS_MS = 5 * 1000;

/**
 * The clock this page mints ids against. Sources feed it through
 * `gpObserveServerTime`; `gpGenerateUniqueId` reads it through `gpCorrectedNow`.
 *
 * `localNow` is a closure over `Date.now` rather than the function itself, so a
 * spec that spies on `Date.now` is actually obeyed. Capturing the reference at
 * module load would bind the real one for the life of the process and the spy
 * would be silently ignored.
 */
const _pageClock = createCorrectedClock({ localNow: () => Date.now() });

/** The server's UTC time in ms, as best this page can tell. */
export function gpCorrectedNow(): number {
    return _pageClock.now();
}

/** Tell this page's clock something about the server's time. */
export function gpObserveServerTime(observation: ServerTimeObservation): void {
    _pageClock.observe(observation);

    // Never write back an observation derived from the stored offset itself. It
    // would refresh its own age on every load, so a record would outlive the
    // assumption it rests on — that nothing has stepped the local clock since —
    // for as long as the browser kept opening pages.
    if (observation.uncertaintyMs < PERSISTED_OFFSET_UNCERTAINTY_MS) {
        rememberOffset(observation);
    }
}

// Both consulted here, at module load, rather than from app bootstrap — and
// that is the whole point. `generate-ids` imports this module, so this body has
// run before any id can be minted, which is the only moment a client clock
// running fast can still be corrected downward. Wiring it into a bootstrap
// sequence instead would put an ordering constraint somewhere an import that
// mints an id at module scope could break.
//
// Order does not matter: the coarser of the two loses on uncertainty either way.
const _persistedObservation = observationFromPersistedOffset(readPersistedOffset(), Date.now());
if (_persistedObservation) {
    gpObserveServerTime(_persistedObservation);
}

const _pageObservation = observationFromPage(readPageTimings());
if (_pageObservation) {
    gpObserveServerTime(_pageObservation);
}

export function createCorrectedClock(options: CorrectedClockOptions): CorrectedClock {

    // What to add to the local clock to get the server's. Zero until we are
    // told otherwise, which makes an uninformed clock the local one. Held as a
    // float: rounding each repayment to whole milliseconds would floor small
    // ones to nothing, and a clock read often enough would never converge.
    let offsetMs = 0;

    // What observations say the offset should be. `offsetMs` moves toward this.
    let targetOffsetMs = 0;

    // The local reading at the last `now()`. Repayment is a function of elapsed
    // local time, never of how often we were asked, so two reads at one instant
    // give one answer.
    let lastLocalMs: number | undefined;

    // Whether a reading has ever gone out. Until one has, nothing can depend on
    // this clock, so a correction in either direction applies as a step. After
    // one has, a backward correction has to be repaid instead — an id carrying
    // it has already been minted.
    let sealed = false;

    // The highest reading handed out. Backstop for a local clock that itself
    // jumps backwards, which no amount of repayment anticipates.
    let highWaterMs: number | undefined;

    // How wrong the observation currently driving `targetOffsetMs` might be.
    // Infinite until there is one, so the first observation of any quality wins.
    let adoptedUncertaintyMs = Infinity;

    // The local clock reading that observation was taken against.
    let adoptedAtClientMs: number | undefined;

    return {
        now() {
            const localMs = options.localNow();
            const elapsedMs = lastLocalMs === undefined ? 0 : Math.max(0, localMs - lastLocalMs);
            lastLocalMs = localMs;
            sealed = true;

            if (targetOffsetMs > offsetMs) {
                // Forward. Nothing already handed out is threatened by jumping
                // ahead, so there is no reason to be gradual about it.
                offsetMs = targetOffsetMs;
            } else if (targetOffsetMs < offsetMs) {
                offsetMs = Math.max(targetOffsetMs, offsetMs - elapsedMs * SLEW_RATE);
            }

            const corrected = Math.round(localMs + offsetMs);
            if (highWaterMs === undefined || corrected > highWaterMs) {
                highWaterMs = corrected;
            }
            return highWaterMs;
        },

        observe(observation: ServerTimeObservation) {
            // A reading is only as good as the local clock it was measured
            // against, and that clock can be stepped by a resume or an NTP
            // correction at any moment. Past the freshness horizon a coarse
            // reading taken now knows more than a fine one taken before.
            const adoptedHasGoneStale = adoptedAtClientMs === undefined
                || observation.clientUtcMs - adoptedAtClientMs > OBSERVATION_FRESHNESS_MS;

            // A source that knows less than the one we are already holding has
            // nothing to add. Ties are adopted, so a steady stream from one
            // source keeps refreshing rather than freezing on its first reading.
            if (observation.uncertaintyMs > adoptedUncertaintyMs && !adoptedHasGoneStale) {
                return;
            }

            adoptedUncertaintyMs = observation.uncertaintyMs;
            adoptedAtClientMs = observation.clientUtcMs;
            targetOffsetMs = observation.serverUtcMs - observation.clientUtcMs;
            if (!sealed) {
                offsetMs = targetOffsetMs;
            }
        },
    };
}
